 
 
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react'
import env from '../../../../../../env.json'
// @ts-expect-error TS7016 -- MIGRATION: not approved, fix when encountered
import { Dropdown, InputText } from 'ui_kit_qmd'
import moment, { type Moment } from 'moment'
import Calendar from 'react-calendar'
import calendarIcon from 'images/common/calendar.png'
import yellowError from 'images/common/yellow-error.svg'
import { type Address } from 'types/types'

import { sortedStatesAddressDropdownList, text } from './utils'
import { PharmacySearch } from './components/pharmacy-search/PharmacySearch'
import { ProviderNote } from './components/provider-note/ProviderNote'
import { useProductOrderContext } from '../../../../context'
import './addressForm.scss'

interface Props {
  savedAddress: Address
  setAddress: (address: Address) => void
  noteError: string
}

export interface AddressError {
  city?: string
  streetAddress1?: string
  zip?: string
}

export interface AddressFormRef {
  setError: (addressError: AddressError) => void
}

const endDateDropDownCustomIconStyle = {
  width: 24,
  height: 24,
  right: 18,
  top: 18,
  transform: 'none',
}

const inputFullContainerStyle = {
  width: '100%',
}

const inputHalfContainerStyle = {
  width: 'auto',
}

export const AddressForm = forwardRef<AddressFormRef, Props>(({ savedAddress, setAddress, noteError }: Props, ref) => {
  const {
    isMoudHomeDelivery,
    orderInfo,
    setOrderInfo,
    isMoudPharmacyPickup,
    isMHTPharmacyPickup,
    isPharmacyHub,
    cartItems,
    isMHT,
  } = useProductOrderContext()
  const [selectedAddress, setSelectedAddress] = useState<Address>(savedAddress)
  const [streetAddress1Error, setStreetAddress1Error] = useState<string>('')
  const [cityError, setCityError] = useState<string>('')
  const [zipCodeError, setZipCodeError] = useState<string>('')
  const [earliestFillDate, setEarliestFillDate] = useState<Moment | undefined>()
  const isPharmacyPickup = isMoudPharmacyPickup || isMHTPharmacyPickup
  const title = isPharmacyPickup ? 'Select pharmacy' : 'Shipping address'
  const isFulfilledByTreat = cartItems[0]?.FulfillmentType === 'treat'
  const isHomeDelivery = isMoudHomeDelivery || !isPharmacyPickup

  const showMHTDeliveryWarning = useMemo(() => {
    if (isMHT) {
      return !!cartItems.find((item) => item.IsControlledSubstance)
    }
    return false
  }, [cartItems, isMHT])
  useImperativeHandle(
    ref,
    () => ({
      setError: (error: AddressError) => {
        setCityError(error.city || '')
        setStreetAddress1Error(error.streetAddress1 || '')
        setZipCodeError(error.zip || '')
      },
    }),
    [],
  )
  const updateAddress = useCallback(
    (update: Address) => {
      setSelectedAddress({ ...update })
      setAddress(update)
    },
    [setAddress],
  )

  return (
    <div className='address-form-container'>
      <div className='address-form-header-container'>
        <div className='address-form-title' id='title'>
          {title}
        </div>

        {isHomeDelivery && isPharmacyHub && (isMoudHomeDelivery || showMHTDeliveryWarning) ? <div className='address-form-home-delivery-warning'>
            <img src={yellowError} alt='Warning icon' />
            <div>Make sure the patient will be there to provide ID and signature to receive it.</div>
          </div> : null}
      </div>
      {isPharmacyPickup ?
        <div className='address-form-pharmacy-container'>
          <PharmacySearch setSelectedAddress={setSelectedAddress} />
          <Dropdown
            isDateCase
            isCalendarOptions
            containerStyle={{ width: '100%' }}
            calendarValue={earliestFillDate ? earliestFillDate.format('MMM DD YYYY') : moment().format('MMM DD YYYY')}
            value={
              earliestFillDate ?
                earliestFillDate.format(`MMM DD YYYY${moment().isSame(earliestFillDate, 'day') ? ' [(Today)]' : ''}`)
              : undefined
            }
            // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
            setCalendarValue={(value) => {
              setEarliestFillDate(moment(value))
              // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
              setOrderInfo({ ...orderInfo, EarliestFillDate: moment(value).format('YYYY-MM-DD') })
              const element = document.getElementById('title')
              setTimeout(() => {
                // @ts-expect-error TS18047 -- MIGRATION: not approved, fix when encountered
                element.click()
              }, 5)
            }}
            dropId='End date*'
            label='Earliest fill date*'
            isShowStar
            customIcon={calendarIcon}
            customIconStyles={endDateDropDownCustomIconStyle}
            minCalendarDate={new Date(moment().format('YYYY-MM-DD HH:mm'))}
            Calendar={Calendar}
          />
        </div>
      : isPharmacyHub ?
        <div className='address-form-address-container'>
          <div className='address-form-address-1-container'>
            <InputText
              containerStyle={{ ...inputFullContainerStyle }}
              label={text.STREET_ADDRESS_1}
              // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
              setValue={(value) => {
                setStreetAddress1Error('')
                updateAddress({ ...selectedAddress, StreetAddress1: value })
              }}
              value={selectedAddress.StreetAddress1}
              isError={streetAddress1Error}
              localError={streetAddress1Error}
              isGoogleAutoComplete
              googleApiKey={env.googleAPIKey}
              onAutocomplete={onAutocomplete}
            />
          </div>
          <InputText
            label={text.STREET_ADDRESS_2}
            containerStyle={{ ...inputFullContainerStyle }}
            view='visitType'
            // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
            setValue={(value) => {
              updateAddress({ ...selectedAddress, StreetAddress2: value })
            }}
            value={selectedAddress.StreetAddress2 || ''}
          />
          <InputText
            label={text.CITY}
            isError={cityError}
            localError={cityError}
            containerStyle={{ ...inputFullContainerStyle }}
            view='visitType'
            // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
            setValue={(value) => {
              setCityError('')
              updateAddress({ ...selectedAddress, City: value })
            }}
            value={selectedAddress.City}
          />
          <div className='address-form-state-zip-row'>
            <Dropdown
              isStateDropdown
              options={sortedStatesAddressDropdownList}
              containerStyle={{ ...inputHalfContainerStyle }}
              label={text.STATE}
              // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
              setValue={(value) => {
                updateAddress({ ...selectedAddress, StateCode: value })
              }}
              value={selectedAddress.StateCode}
            />
            <InputText
              containerStyle={{ ...inputHalfContainerStyle }}
              label={text.ZIP_CODE}
              view='visitType'
              isError={zipCodeError}
              localError={zipCodeError}
              // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
              setValue={(value) => {
                const filteredValue = value
                  .split('')
                   
                  // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
                  .filter((character) => !isNaN(character) || character === '-')
                  .join('')
                setZipCodeError('')
                updateAddress({ ...selectedAddress, Zip: filteredValue })
              }}
              value={selectedAddress.Zip}
            />
          </div>
        </div>
      : <div className='address-form-info-banner'>
          <div className='address-form-info-banner-content'>
            <div className='address-form-info-banner-title'>Patient will confirm shipping address</div>
            <div className='address-form-info-banner-text'>
              The patient will confirm shipping via the QuickMD patient app
            </div>
          </div>
        </div>
      }

      <div>
        <div className='address-form-note'>
          {`Add Note for ${isFulfilledByTreat ? 'Pharmacy' : 'QuickMD Support'}`} (optional)
        </div>
        <ProviderNote noteError={noteError} />
      </div>
    </div>
  )

  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  function onAutocomplete({ address_components }) {
    const address: Address = {
      StateCode: '',
      StreetAddress1: '',
      StreetAddress2: '',
      Zip: '',
      City: '',
      CountryCode: 'US',
    }
    // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
    address_components.forEach(({ short_name, types }) => {
      if (types.includes(text.STREET_NUMBER_KEY)) {
        address.StreetAddress1 = short_name
      }

      if (types.includes(text.ROUTE_KEY)) {
        address.StreetAddress1 = `${address.StreetAddress1} ${short_name}`
      }

      if (types.includes(text.LOCALITY_KEY)) {
        address.City = short_name
      }

      if (types.includes(text.ADMINISTRATIVE_AREA_LEVEL_1_KEY)) {
        address.StateCode = short_name
      }
      if (types.includes(text.COUNTRY)) {
        address.CountryCode = short_name
      }
      if (types.includes(text.POSTAL_CODE_KEY)) {
        address.Zip = short_name
      }
    })
    setSelectedAddress(address)
    setCityError('')
    setZipCodeError('')
    setStreetAddress1Error('')
    setAddress(address)
  }
})
